"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ClassIcon } from "@/components/icons/ClassIcon";
import { SpecIcon } from "@/components/icons/SpecIcon";
import { BattleNetIcon } from "@/components/icons/BattleNetIcon";
import { CLASSES } from "@/lib/classes";
import { useCharState, setClass, setPlayedSpec } from "@/lib/state";
import { useAuth } from "@/lib/auth";
import { classColorVar, ROLE_LABEL, ROLE_COLOR } from "@/lib/wow";
import { cn } from "@/lib/utils";

export default function LandingPage() {
  const router = useRouter();
  const state = useCharState();
  const auth = useAuth();
  const [pickedClassID, setPickedClassID] = useState<number | null>(null);
  const cls = pickedClassID
    ? CLASSES.find((c) => c.id === pickedClassID)
    : null;

  // Signed-in users skip the marketing/onboarding screen — they already
  // know what this is. router.replace (not push) so the back button
  // doesn't trap them on the landing page.
  useEffect(() => {
    if (auth.state === "signed-in") {
      router.replace("/dashboard");
    }
  }, [auth.state, router]);

  // While we're verifying auth or about to redirect, render nothing.
  // Prevents the class picker from flashing for a half-second on every
  // signed-in page load.
  if (auth.state === "loading" || auth.state === "signed-in") return null;

  function selectClass(id: number) {
    setClass(id);
    setPickedClassID(id);
  }

  function selectSpec(specID: number) {
    setPlayedSpec(specID);
    router.push("/recommendations");
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
      {/* Headline */}
      <div className="text-center">
        <p className="eyebrow">Midnight Season 1</p>
        <h1 className="mt-4 font-display text-4xl font-medium tracking-tight text-fg sm:text-6xl">
          Where should you spend
          <br />
          your <span className="text-accent">bonus rolls</span>?
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-fg-muted sm:text-base">
          Pick your class and spec, set your BiS targets, and BonusRoll ranks every raid boss
          and dungeon by the probability of hitting an upgrade.
        </p>
      </div>

      {!cls ? (
        <ClassGrid currentID={state.classID} onPick={selectClass} />
      ) : (
        <SpecGrid
          classDef={cls}
          currentID={state.playedSpecID}
          onPick={selectSpec}
          onBack={() => setPickedClassID(null)}
        />
      )}

      {/* OR rule + Battle.net */}
      <div className="mt-10 flex flex-col items-center gap-4">
        <div className="flex items-center gap-3 text-xs text-fg-subtle">
          <span className="h-px w-8 bg-border" />
          <span className="font-mono uppercase tracking-wider">or</span>
          <span className="h-px w-8 bg-border" />
        </div>
        <Link
          href="/login"
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg transition-colors"
        >
          <BattleNetIcon size={16} />
          Sign in with Battle.net to import gear and persist exclusions
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {/* Three-column explainer */}
      <div className="mt-16 grid gap-8 sm:grid-cols-3">
        <Explainer
          step="01"
          title="Pick class & spec"
          body="Or sign in with Battle.net to import your characters automatically."
        />
        <Explainer
          step="02"
          title="Configure BiS"
          body="For each of 14 slots: skip, accept any catalyzable piece, or pick specific items."
        />
        <Explainer
          step="03"
          title="Spend wisely"
          body="See every pool ranked by P(BiS-hit). Mark items you've already rolled to keep recommendations sharp."
        />
      </div>
    </div>
  );
}

function Explainer({ step, title, body }: { step: string; title: string; body: string }) {
  return (
    <div>
      <p className="font-mono tabular text-xs text-accent-dim">{step}</p>
      <h3 className="mt-2 font-display text-lg font-medium text-fg">{title}</h3>
      <p className="mt-1.5 text-sm text-fg-muted leading-relaxed">{body}</p>
    </div>
  );
}

function ClassGrid({
  currentID,
  onPick,
}: {
  currentID: number;
  onPick: (id: number) => void;
}) {
  return (
    <div className="mt-12">
      <p className="eyebrow text-center mb-5">Choose your class</p>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-7 sm:gap-3">
        {CLASSES.map((c) => {
          const active = c.id === currentID;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onPick(c.id)}
              className={cn(
                "group flex flex-col items-center gap-2 p-3 rounded-md border transition-colors",
                "bg-surface/40 hover:bg-surface-2",
                active ? "border-accent" : "border-border hover:border-border-strong",
              )}
            >
              <ClassIcon classToken={c.token} size={48} />
              <span
                className="text-xs font-medium text-center leading-tight"
                style={{ color: classColorVar(c.token) }}
              >
                {c.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SpecGrid({
  classDef,
  currentID,
  onPick,
  onBack,
}: {
  classDef: typeof CLASSES[number];
  currentID: number;
  onPick: (id: number) => void;
  onBack: () => void;
}) {
  return (
    <div className="mt-12">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-fg-subtle hover:text-fg transition-colors font-mono uppercase tracking-wider"
        >
          ← Change class
        </button>
        <p
          className="text-sm font-display"
          style={{ color: classColorVar(classDef.token) }}
        >
          {classDef.name}
        </p>
        <span className="w-[110px]" />
      </div>

      <div className={cn(
        "grid gap-3",
        classDef.specs.length === 4 ? "sm:grid-cols-4" : `sm:grid-cols-${classDef.specs.length}`,
      )}>
        {classDef.specs.map((s) => {
          const active = s.id === currentID;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s.id)}
              className={cn(
                "group flex flex-col items-center gap-3 p-5 rounded-md border transition-all",
                "bg-surface hover:bg-surface-2",
                active ? "border-accent" : "border-border hover:border-border-strong",
              )}
            >
              <SpecIcon specID={s.id} size={56} />
              <div className="text-center">
                <p
                  className="font-display text-base font-medium"
                  style={{ color: classColorVar(classDef.token) }}
                >
                  {s.name}
                </p>
                <p className={cn("font-mono text-[10px] uppercase tracking-wider mt-1", ROLE_COLOR[s.role])}>
                  {ROLE_LABEL[s.role]}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-8 text-center">
        <Button
          variant="primary"
          size="lg"
          onClick={() => onPick(currentID)}
        >
          Continue with {classDef.specs.find((s) => s.id === currentID)?.name ?? classDef.specs[0]?.name}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
